import React from 'react';
import { ConfigProvider } from 'antd';
import { useTranslation } from 'react-i18next';
import { antdLocaleFor } from '../services/localization/antdLocale';
import useTheme from '../hooks/context/useTheme';
import useBrand from '../hooks/context/useBrand';
import ColorHelpers from '../services/helpers/ColorHelpers';

export default function AntConfigProvider({ children }) {
  const theme = useTheme();
  const brand = useBrand();

  // Subscribing re-renders on languageChanged, so antd's strings switch at runtime.
  const { i18n } = useTranslation();

  const colorPrimary = brand?.primaryColor ?? theme.color.brand.primary;
  // antd derives its "light tint" tokens (selected menu item, active option, …)
  // by generating a palette from colorPrimary, which inverts for near-black
  // seeds — the neutral white-label #161616 turned the selected sidebar item
  // into a solid black pill. Mixing with white is deterministic for any brand
  // color and matches the generated tint for the FoodAmigos green.
  const colorPrimaryBg = ColorHelpers.tint(colorPrimary, 0.92);
  const colorPrimaryBgHover = ColorHelpers.tint(colorPrimary, 0.85);

  return (
    <ConfigProvider
      locale={antdLocaleFor(i18n.language).antd}
      theme={{
        token: {
          colorPrimary,
          ...(colorPrimaryBg && { colorPrimaryBg, colorPrimaryBgHover }),
          fontSizeHeading1: theme.fonts.fontSizes.h1,
          fontSizeHeading2: theme.fonts.fontSizes.h2,
          fontSizeHeading3: theme.fonts.fontSizes.h3,
          fontSizeHeading4: theme.fonts.fontSizes.h4,
          fontSizeHeading5: theme.fonts.fontSizes.h5,
          fontFamily: theme.fonts.fontFamily.inter,
          colorBorder: theme.color.brand.inkGrey.grey_2,
          // antd's defaults for secondary and disabled text are rgba blacks at
          // 45%/25% opacity, which land around 2.8:1 and 1.7:1 on white — well
          // under the WCAG AA bar. Disabled buttons (the greyed-out Save) were
          // called out specifically in Artur's round-2 feedback §3.
          colorTextSecondary: theme.color.brand.inkGrey.grey_4,
          colorTextDescription: theme.color.brand.inkGrey.grey_3,
          colorTextDisabled: theme.color.brand.inkGrey.grey_4,
          colorTextPlaceholder: theme.color.brand.inkGrey.grey_3,
        },
        components: {
          Modal: {
            zIndexPopupBase: 1000,
          },
          DatePicker: {
            zIndexPopupBase: 5000,
          },
          Card: {
            // Preserves the elevated card look previously provided by CoreUI's `.card` styles.
            boxShadow: '0px 6px 24px rgba(167, 186, 197, 0.3)',
            borderRadiusLG: 12,
          },
        },
      }}
    >
      {children}
    </ConfigProvider>
  );
}
